import React from "react";
import { KeyTerm, ImportantNote } from "../note-components";
import type { UnitNotes } from "../types";

const worldHistoryUnit1Notes: UnitNotes = {
  unitId: "unit-1",
  unitName: "Unit 1: The Global Tapestry (c. 1200 – c. 1450)",
  examWeight: "8–10%",
  focus: "How did states establish their authority, and how did regional cultures and beliefs shape society during this period?",
  sections: [
    {
      id: "topic-1-1",
      title: "Topic 1.1: Developments in East Asia",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏯 Government and State Building (The Song Dynasty)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The Song Dynasty (960–1279) is considered a "Golden Age" of China. It was defined by political stability, intellectual achievement, and economic power.
            </p>
            <div className="space-y-3">
              <KeyTerm 
                term="Imperial Bureaucracy" 
                definition="A vast organization in which appointed officials carried out the empire's policies. This system had existed since the Qin Dynasty but was expanded under the Song."
              />
              <KeyTerm 
                term="Civil Service Exams" 
                definition="To work in the bureaucracy, men had to pass rigorous exams based on Confucian texts. Because officials were chosen based on exam scores rather than lineage, the Song allowed for some upward social mobility (though the wealthy still had an advantage in affording education)."
              />
              <KeyTerm 
                term="Mandate of Heaven" 
                definition="The traditional Chinese philosophical concept that the emperor gained the right to rule from the heavens/gods; if a ruler was corrupt or incompetent, heaven would withdraw the mandate (often signaled by natural disasters or peasant revolts)."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📚 Cultural Traditions</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">🎋 Confucianism</h4>
                <p className="text-sm text-slate-700 mt-1">The core philosophy of Song China. It focused on <strong>Filial Piety</strong> (extreme respect for one's parents and ancestors) and <strong>Social Hierarchy</strong> (Five Relationships: Ruler/Subject, Father/Son, etc.).</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">☯️ Neo-Confucianism</h4>
                <p className="text-sm text-slate-700 mt-1">A syncretic philosophy that evolved in China between 770 and 840. It blended rational Confucian ethics with the abstract/spiritual ideas of Daoism and Buddhism. It became very popular in Japan, Korea, and Vietnam.</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🙏 Buddhism in China</h4>
                <p className="text-sm text-slate-700 mt-1">Arrived from India via the Silk Roads. <strong>Theravada</strong> focused on personal spiritual growth/meditation (stricter). <strong>Mahayana</strong> focused on spiritual growth for all beings and service; stronger in China/Korea. <strong>Zen (Chan) Buddhism</strong> mixed Buddhist doctrines with Daoist traditions.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💰 Economic Developments</h3>
            <div className="grid md:grid-cols-2 gap-3 mb-4">
              <div className="bg-gradient-to-br from-green-100 to-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">🌾 Champa Rice</h4>
                <p className="text-sm text-slate-700 mt-1">A fast-ripening, drought-resistant rice strain from Vietnam. It allowed for <strong>two harvests per year</strong>, leading to a massive population boom.</p>
              </div>
              <div className="bg-gradient-to-br from-blue-100 to-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🚢 Grand Canal</h4>
                <p className="text-sm text-slate-700 mt-1">An internal waterway transportation system that extended over 30,000 miles. It linked the food-rich south (Yangtze) to the population-dense north.</p>
              </div>
            </div>
            <div className="space-y-3">
              <KeyTerm 
                term="Proto-Industrialization" 
                definition="China began producing more goods than it could sell (specifically porcelain, silk, and textiles) using community-based production (artisans in homes), not factories."
              />
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">⚡ Key Technologies</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>Gunpowder:</strong> Originally for fireworks, eventually used for weaponry</li>
                  <li><strong>Compass:</strong> Revolutionized maritime trade</li>
                  <li><strong>Woodblock Printing:</strong> Allowed for mass production of texts and study guides for the Civil Service Exams</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">👥 Social Structures</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-gradient-to-br from-amber-100 to-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">📖 Scholar Gentry</h4>
                <p className="text-sm text-slate-700 mt-1">A new social class created by the bureaucratic expansion; they outnumbered the aristocracy and became the most influential class.</p>
              </div>
              <div className="bg-gradient-to-br from-pink-100 to-pink-50 p-4 rounded-lg border border-pink-200">
                <h4 className="font-bold text-pink-900">👧 Foot Binding</h4>
                <p className="text-sm text-slate-700 mt-1">A practice of wrapping young girls' feet to break bones and stunt growth. It was a status symbol (women didn't have to work) and a tool of restriction/control.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-2",
      title: "Topic 1.2: Developments in Dar al-Islam",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🕌 Political Fragmentation of the Abbasids</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The Abbasid Caliphate (based in Baghdad) was fracturing. New Islamic political entities emerged, often dominated by Turkic peoples (pastoralists from Central Asia).
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">⚔️ Seljuk Empire</h4>
                <p className="text-sm text-slate-700 mt-1">Turkic warriors from Central Asia. They conquered the Middle East and claimed the title of <strong>Sultan</strong> (reducing the Abbasid Caliph to a religious figurehead).</p>
              </div>
              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-4 rounded-lg border-l-4 border-teal-400">
                <h4 className="font-bold text-teal-900">🏛️ Mamluk Sultanate (Egypt)</h4>
                <p className="text-sm text-slate-700 mt-1">Mamluks were enslaved ethnic Turks purchased by Arabs to serve as soldiers. They eventually seized control of the government in Egypt and established a powerful empire (1250–1517) that facilitated trade in cotton and sugar.</p>
              </div>
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-4 rounded-lg border-l-4 border-indigo-400">
                <h4 className="font-bold text-indigo-900">🇮🇳 Delhi Sultanate (India)</h4>
                <p className="text-sm text-slate-700 mt-1">An Islamic empire based in Delhi that ruled over large parts of northern India. It caused tension between the ruling Muslims and the majority Hindu population. They imposed the <strong>Jizya</strong> (tax on non-Muslims).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎓 Cultural and Intellectual Innovations</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🔭 Nasir al-Din al-Tusi</h4>
                <p className="text-sm text-slate-700 mt-1">A Persian scholar who contributed to astronomy, law, logic, ethics, mathematics, philosophy, and medicine. He built the most advanced observatory in the world.</p>
              </div>
              <div className="bg-gradient-to-r from-pink-50 to-rose-50 p-4 rounded-lg border border-pink-200">
                <h4 className="font-bold text-pink-900">✍️ 'A'ishah al-Ba'uniyyah</h4>
                <p className="text-sm text-slate-700 mt-1">A female Sufi master and poet; her writings highlight the high level of learning achieved by some women in the Islamic world.</p>
              </div>
              <KeyTerm 
                term="House of Wisdom (Baghdad)" 
                definition="A major intellectual center where scholars preserved and translated distinct Greek and Roman texts (like Aristotle) into Arabic, saving them from being lost during the European Dark Ages."
              />
              <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">🧘 Sufism</h4>
                <p className="text-sm text-slate-700 mt-1">An Islamic mystical trend that focused on a personal, emotional connection to God (introspection) rather than strict study of the Quran. Sufi missionaries were highly effective at converting people in India and Southeast Asia because they accommodated local gods and beliefs.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-3",
      title: "Topic 1.3: Developments in South & Southeast Asia",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🇮🇳 South Asia (India)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              India experienced political fragmentation and religious diversity during this period.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-pink-50 to-rose-50 p-4 rounded-lg border-l-4 border-pink-400">
                <h4 className="font-bold text-pink-900">🏰 Delhi Sultanate (1206–1526)</h4>
                <p className="text-sm text-slate-700 mt-1">An Islamic empire based in Delhi that ruled over large parts of northern India. It caused tension between the ruling Muslims and the majority Hindu population.</p>
              </div>
              <div className="bg-gradient-to-r from-red-50 to-pink-50 p-4 rounded-lg border-l-4 border-red-400">
                <h4 className="font-bold text-red-900">🏛️ Vijayanagara Empire (1336–1646)</h4>
                <p className="text-sm text-slate-700 mt-1">"The Victorious City." A Hindu kingdom in Southern India established by two brothers sent by the Delhi Sultanate who eventually reconverted to Hinduism.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">💝 Bhakti Movement</h4>
                <p className="text-sm text-slate-700 mt-1">A Hindu reform movement that emphasized a personal emotional attachment to a specific god (like Shiva or Vishnu) rather than studying texts or performing rituals. It appealing to the lower castes and women because it did not discriminate. (Similar to Sufi Muslims).</p>
              </div>
              <KeyTerm 
                term="Caste System" 
                definition="Highly rigid social hierarchy in India. It remained the strongest social continuity in India, even under Muslim rule."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏝️ Southeast Asia</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              This region was strategically important for trade (Straits of Malacca).
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-emerald-50 to-green-50 p-4 rounded-lg border-l-4 border-emerald-400">
                <h4 className="font-bold text-emerald-900">🏝️ Srivijaya Empire (670–1025)</h4>
                <p className="text-sm text-slate-700 mt-1">A Hindu/Buddhist kingdom based on Sumatra. It grew wealthy by taxing trade ships passing through the strait.</p>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-lime-50 p-4 rounded-lg border-l-4 border-green-400">
                <h4 className="font-bold text-green-900">🗾 Majapahit Kingdom (1293–1520)</h4>
                <p className="text-sm text-slate-700 mt-1">Based on Java; sustained power by controlling sea routes. Buddhist.</p>
              </div>
              <div className="bg-gradient-to-r from-teal-50 to-emerald-50 p-4 rounded-lg border-l-4 border-teal-400">
                <h4 className="font-bold text-teal-900">🏯 Khmer Empire (Angkor Kingdom, 802–1431)</h4>
                <p className="text-sm text-slate-700 mt-1">Located in present-day Cambodia/Laos. Built <strong>Angkor Wat</strong>, a massive temple complex showing the conversion from Hinduism to Buddhism (artwork displays both). It demonstrates the sophisticated irrigation and drainage systems of the empire.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-4",
      title: "Topic 1.4: Developments in the Americas",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🗽 North America</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-gradient-to-br from-yellow-100 to-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">🏔️ Mississippian Culture</h4>
                <p className="text-sm text-slate-700 mt-1">Built massive earthen mounds (example: <strong>Cahokia</strong>, a trading city near present-day St. Louis with 40,000 people). It had a rigid class structure with a chief called the Great Sun.</p>
              </div>
              <div className="bg-gradient-to-br from-amber-100 to-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">🏜️ Chaco & Mesa Verde</h4>
                <p className="text-sm text-slate-700 mt-1">Located in the arid US Southwest. Built large housing structures using stones and clay (pueblos) and cliff dwellings.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌎 Central America (Mesoamerica)</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border-l-4 border-orange-400">
                <h4 className="font-bold text-orange-900">🔢 Maya</h4>
                <p className="text-sm text-slate-700 mt-1">City-states (not a unified empire) ruled by a king and consisting of a city and surrounding territory. Innovations: Concept of zero, complex writing system, rubber.</p>
              </div>
              <div className="bg-gradient-to-r from-red-50 to-rose-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">🏛️ Aztecs (Mexica)</h4>
                <p className="text-sm text-slate-700 mt-1">Originally hunter-gatherers who founded <strong>Tenochtitlan</strong> (modern Mexico City).</p>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="bg-gradient-to-br from-red-100 to-red-50 p-4 rounded-lg border border-red-200">
                  <h5 className="font-bold text-red-900">💰 Tribute System</h5>
                  <p className="text-xs text-slate-700 mt-1">Conquered people had to pay tribute (money, land, military service, goods) to the Aztecs. This allowed the Aztecs to rule a diverse empire without direct administrative control.</p>
                </div>
                <div className="bg-gradient-to-br from-green-100 to-green-50 p-4 rounded-lg border border-green-200">
                  <h5 className="font-bold text-green-900">🌾 Chinampas</h5>
                  <p className="text-xs text-slate-700 mt-1">"Floating gardens" to increase food production on Lake Texcoco.</p>
                </div>
              </div>
              <KeyTerm 
                term="Aztec Theocracy" 
                definition="Rule by religious leaders. Human sacrifice was a central political and religious ritual."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏔️ South America (Andes)</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-rose-50 p-4 rounded-lg border-l-4 border-red-400">
                <h4 className="font-bold text-red-900">🦙 Inca Empire</h4>
                <p className="text-sm text-slate-700 mt-1">Extended from Ecuador to Chile.</p>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="bg-gradient-to-br from-orange-100 to-orange-50 p-4 rounded-lg border border-orange-200">
                  <h5 className="font-bold text-orange-900">⚒️ Mit'a System</h5>
                  <p className="text-xs text-slate-700 mt-1">A mandatory public service (labor tax). Conquered people had to perform agricultural or construction labor for the state (e.g., building roads).</p>
                </div>
                <div className="bg-gradient-to-br from-green-100 to-green-50 p-4 rounded-lg border border-green-200">
                  <h5 className="font-bold text-green-900">🌾 Warru Warru</h5>
                  <p className="text-xs text-slate-700 mt-1">Agricultural technique (raised beds with channels) to collect rain.</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="bg-gradient-to-br from-purple-100 to-purple-50 p-4 rounded-lg border border-purple-200">
                  <h5 className="font-bold text-purple-900">📊 Quipu</h5>
                  <p className="text-xs text-slate-700 mt-1">Knotted strings used for recording numerical information (census, trade).</p>
                </div>
                <div className="bg-gradient-to-br from-blue-100 to-blue-50 p-4 rounded-lg border border-blue-200">
                  <h5 className="font-bold text-blue-900">🛣️ Carpa Nan</h5>
                  <p className="text-xs text-slate-700 mt-1">A massive roadway system (25,000 miles) used by the military and government runners.</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-5",
      title: "Topic 1.5: Developments in Africa",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Political Structures</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">👨‍👩‍👧‍👦 Kin-Based Networks</h4>
                <p className="text-sm text-slate-700 mt-1">Unlike Asia/Europe, Sub-Saharan Africa often did not have centralized governments. Families governed themselves. A male chief mediated conflicts.</p>
              </div>
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">🏛️ Hausa Kingdoms</h4>
                <p className="text-sm text-slate-700 mt-1">Seven states in modern Nigeria connected by kinship ties. They had no central authority (which made them vulnerable to outside domination). They were key players in the Trans-Saharan trade network.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">👑 Powerful States</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 p-4 rounded-lg border-l-4 border-yellow-500">
                <h4 className="font-bold text-yellow-900">🏰 Great Zimbabwe</h4>
                <p className="text-sm text-slate-700 mt-1">Located in Southeast Africa. It became wealthy through agriculture and the gold trade (connected to the Indian Ocean trade network). Built massive stone walls without mortar.</p>
              </div>
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-4 rounded-lg border-l-4 border-amber-500">
                <h4 className="font-bold text-amber-900">✝️ Ethiopia (Axum)</h4>
                <p className="text-sm text-slate-700 mt-1">A Christian kingdom in a predominantly Muslim region (East Africa). It developed <strong>Rock-Hewn Churches</strong>, blending traditional African architecture with Christian symbolism.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎤 Social Aspects</h3>
            <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
              <h4 className="font-bold text-orange-900">🎵 Griots</h4>
              <p className="text-sm text-slate-700 mt-1">Oral storytellers who preserved history and lineage. They were feared and respected for their ability to sing your success or your downfall.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-6",
      title: "Topic 1.6: Developments in Europe",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏰 Political Structures: Feudalism</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Following the fall of Rome, Europe fractured into a decentralized political organization based on a system of exchanges of land for loyalty.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-slate-50 to-gray-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">⚔️ Feudalism Hierarchy</h4>
                <div className="space-y-2 mt-2">
                  <div className="flex items-center gap-2">
                    <span className="bg-slate-300 text-slate-800 font-bold rounded-full w-6 h-6 flex items-center justify-center text-xs">1</span>
                    <p className="text-sm text-slate-700"><strong>Monarch</strong> granted land (fiefs) to Lords</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="bg-slate-300 text-slate-800 font-bold rounded-full w-6 h-6 flex items-center justify-center text-xs">2</span>
                    <p className="text-sm text-slate-700"><strong>Lords</strong> provided land to Knights</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="bg-slate-300 text-slate-800 font-bold rounded-full w-6 h-6 flex items-center justify-center text-xs">3</span>
                    <p className="text-sm text-slate-700"><strong>Knights</strong> provided protection to Lords and the King</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="bg-slate-300 text-slate-800 font-bold rounded-full w-6 h-6 flex items-center justify-center text-xs">4</span>
                    <p className="text-sm text-slate-700"><strong>Peasants (Serfs)</strong> worked the land in exchange for protection</p>
                  </div>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <KeyTerm 
                  term="Manorialism" 
                  definition="The economic side of feudalism. Large fiefs (manors) were self-sufficient."
                />
                <KeyTerm 
                  term="Serfdom" 
                  definition="Serfs were peasants tied to the land. They were not slaves (could not be sold), but could not leave without permission or marry without approval."
                />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">👑 Changing Political Power (Late Middle Ages)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Kings began to hire massive bureaucracies and armies (paid for by taxes), reducing the power of feudal lords.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-slate-50 to-gray-50 p-4 rounded-lg border-l-4 border-slate-400">
                <h4 className="font-bold text-slate-900">🇫🇷 Estates-General (France)</h4>
                <p className="text-sm text-slate-700 mt-1">A body to advise the king, representing the three legal classes (clergy, nobility, commoners).</p>
              </div>
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 p-4 rounded-lg border-l-4 border-gray-400">
                <h4 className="font-bold text-gray-900">📜 Magna Carta (1215)</h4>
                <p className="text-sm text-slate-700 mt-1">King John of England was forced to sign this, giving rights to nobles (e.g., right to a jury trial).</p>
              </div>
              <KeyTerm 
                term="English Parliament" 
                definition="Established in 1265; increased the rights of the English nobility, but not the commoners."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⛪ Religious Power</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-violet-50 to-purple-50 p-4 rounded-lg border-l-4 border-violet-400">
                <h4 className="font-bold text-violet-900">✝️ The Roman Catholic Church</h4>
                <p className="text-sm text-slate-700 mt-1">The most powerful institution in Europe. It held the most land, controlled education/art, and was the only unifying force in a fragmented region.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-4 rounded-lg border-l-4 border-purple-400">
                <h4 className="font-bold text-purple-900">⚔️ The Crusades (1095–1200s)</h4>
                <p className="text-sm text-slate-700 mt-1">Military campaigns to take back the Holy Land from Muslims. <strong>Impact:</strong> Failed militarily, but promoted cultural exchange (fabrics, spices, medicine) and increased demand for Asian goods in Europe.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📈 Social and Economic Change</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">🏪 Bourgeoisie (Burghers)</h4>
                <p className="text-sm text-slate-700 mt-1">A new middle class (shopkeepers, merchants, craftspeople) that emerged as trade expanded.</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">❄️ Little Ice Age</h4>
                <p className="text-sm text-slate-700 mt-1">Lower temperatures reduced agricultural productivity, leading to less population growth and social unrest.</p>
              </div>
              <div className="bg-gradient-to-r from-pink-50 to-rose-50 p-4 rounded-lg border border-pink-200">
                <h4 className="font-bold text-pink-900">🎨 The Renaissance</h4>
                <p className="text-sm text-slate-700 mt-1">A revival of interest in classical Greek and Roman literature, art, culture, and civic virtue. It marked the end of the "Dark Ages."</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-7",
      title: "Topic 1.7: Comparison in the Period c. 1200 – c. 1450",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ State Building Comparisons</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-br from-blue-100 to-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🏯 Song China</h4>
                <p className="text-sm text-slate-700 mt-1">Maintained power through a centralized bureaucracy, the Mandate of Heaven, and the Civil Service Exam.</p>
              </div>
              <div className="bg-gradient-to-br from-green-100 to-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">🕌 Abbasid Caliphate</h4>
                <p className="text-sm text-slate-700 mt-1">Fragmented; relied on Mamluks and Seljuks (military prowess) to maintain order.</p>
              </div>
              <div className="bg-gradient-to-br from-purple-100 to-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">⛩️ Americas (Aztecs/Incas)</h4>
                <p className="text-sm text-slate-700 mt-1">Used military conquest and centralized systems (tribute/Mit'a) to manage diverse populations.</p>
              </div>
              <div className="bg-gradient-to-br from-orange-100 to-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🏰 Europe</h4>
                <p className="text-sm text-slate-700 mt-1">Decentralized feudalism slowly gave way to centralized monarchies by 1450.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🙏 Religion and State Building</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">☪️ Islam</h4>
                <p className="text-sm text-slate-700 mt-1">Provided a system of law (Sharia) and a shared language (Arabic) that united diverse regions (Afro-Eurasia).</p>
              </div>
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">🎋 Confucianism</h4>
                <p className="text-sm text-slate-700 mt-1">Provided a justification for rule (Mandate of Heaven) and a bureaucratic method (Exam system) in China.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">🙏 Hinduism/Buddhism</h4>
                <p className="text-sm text-slate-700 mt-1">Used by rulers in South/Southeast Asia (like the Khmer) to consolidate power and display wealth (temples).</p>
              </div>
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">✝️ Catholicism</h4>
                <p className="text-sm text-slate-700 mt-1">Provided the only structure and organization in fragmented Europe.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛣️ Trade and Technology</h3>
            <ImportantNote>
              While this Unit focuses on State Building, trade routes (<strong>Silk Road, Trans-Saharan, Indian Ocean</strong>) spread religion and technology (Champa rice, paper, gunpowder) that allowed these states to grow. <strong>Note:</strong> Trade is the primary focus of Unit 2.
            </ImportantNote>
          </section>
        </div>
      )
    }
  ]
};

export default worldHistoryUnit1Notes;
